import { useCallback, type MutableRefObject } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { callIdSelector } from 'features/call/call-base/store/selectors';
import { handleError } from 'features/notification/store';
import {
  SignMailDaoService,
  SignMailRecorderService,
} from 'features/signmail/services';
import {
  handleMailboxFull,
  recordingStarted,
  resetSignMailState,
} from 'features/signmail/store';
import { ParticipantsStatusEventBus } from 'features/call/call-status/services';
import { useMediaStream } from 'features/media-streams/hooks';

export const useSignMailRecording = () => {
  const { getInterpVideoStream } = useMediaStream();
  const service = SignMailRecorderService.getInstance();
  const callId = useAppSelector(callIdSelector);
  const dispatch = useAppDispatch();
  const { signmailFileMediaType } = useFlags();

  const start = useCallback(
    async (videoRef: MutableRefObject<HTMLVideoElement | null>) => {
      const videoStream = await getInterpVideoStream();
      if (!videoStream || !videoRef.current) {
        return false;
      }
      service.setVideoStream(videoStream);

      if (signmailFileMediaType) {
        service.setMimeType(signmailFileMediaType as string);
      }

      try {
        const { UploadURL: uploadUrl, MailboxFull } =
          await SignMailDaoService.getUploadUrl(callId);

        if (MailboxFull) {
          dispatch(handleMailboxFull());
          return false;
        }

        if (!videoRef.current) {
          return false;
        }

        const recordingStream = await service.start();
        const videoElement = videoRef.current;
        if (!videoElement) {
          service.destroy();
          return false;
        }

        videoElement.srcObject = recordingStream;
        dispatch(recordingStarted({ uploadUrl }));
        return true;
      } catch (error) {
        ParticipantsStatusEventBus.deaf.$disconnectSignMail.next();
        dispatch(
          handleError({
            error,
            methodName: 'useSignMailRecording.start',
          })
        );
        dispatch(resetSignMailState());
        service.destroy();
        return false;
      }
    },
    [getInterpVideoStream, service, signmailFileMediaType, callId, dispatch]
  );
  return {
    start,
  };
};
